import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Picker, Toast } from 'antd-mobile';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { sendVerifyCode } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { clearCampaignFromStorage, mergeRegisterContextSearch, overlayIfRegisterKeysStale, saveCampaignToStorage, saveReferToStorage } from '../utils/registerContext';
import { getShopId } from '../utils/shopId';
import { readWsTokenFromSearchParams } from '../utils/wsToken';
import { markRegistrationRefreshPending } from '../utils/registrationSession';
import { isValidInternationalPhone } from '../utils/phone';
import CountryCodeSelect, { normalizeSupportedCountryCode } from '../components/CountryCodeSelect';
import './RegisterPage.css';

const COUNTDOWN_SECONDS = 60;

const genderOptions = [
    { label: 'Male', value: 1 },
    { label: 'Female', value: 0 },
    { label: 'Other', value: 2 },
];

export default function RegisterPage() {
    const { loginFromWsApiResult, isAuthenticated, initialized } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [genderPickerVisible, setGenderPickerVisible] = useState(false);
    const [datePickerVisible, setDatePickerVisible] = useState(false);
    const [countryCode, setCountryCode] = useState('+60');
    const [name, setName] = useState('');
    const [gender, setGender] = useState([]);
    const [dateOfBirth, setDateOfBirth] = useState(null);
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [referId, setReferId] = useState('');
    const [campaign, setCampaign] = useState('');
    const [shopId, setShopId] = useState(null);

    const [marketingOptIn, setMarketingOptIn] = useState(true);
    const [sending, setSending] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [verificationId, setVerificationId] = useState(null);
    const timerRef = useRef(null);
    const registrationJustSucceeded = useRef(false);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    // 同步扫码参数到地址栏并从 URL ∪ localStorage 预填表单（仅 id/referId 实质变化时再 replace）
    useEffect(() => {
        const next = overlayIfRegisterKeysStale(searchParams);
        const effectiveParams = next ?? searchParams;

        if (next) {
            navigate({ pathname: '/register', search: next.toString() }, { replace: true });
        }

        const countryCodeParam = effectiveParams.get('countryCode');
        const phoneParam = effectiveParams.get('phone');
        const codeParam = effectiveParams.get('code');
        const referIdParam = effectiveParams.get('referId');
        const campaignParam = effectiveParams.get('campaign');

        if (countryCodeParam) {
            setCountryCode(normalizeSupportedCountryCode(countryCodeParam));
        }
        if (phoneParam) {
            setPhone(phoneParam);
        }
        if (codeParam) {
            setCode(codeParam);
        }
        if (referIdParam) {
            saveReferToStorage(referIdParam);
            setReferId(referIdParam);
        }
        if (campaignParam) {
            saveCampaignToStorage(campaignParam);
            setCampaign(campaignParam);
        }

        const capturedShopId = getShopId(effectiveParams);
        if (capturedShopId) {
            setShopId(capturedShopId);
        }
    }, [navigate, searchParams]);

    const handleSendCode = async () => {
        if (!isValidInternationalPhone(countryCode, phone)) {
            Toast.show({ content: 'Please enter a valid phone number' });
            return;
        }
        if (countdown > 0) {
            return;
        }
        setSending(true);
        try {
            // 统一使用 /send-code 接口，后端会根据配置自动选择验证方式：
            // - ycloud: 返回包含 id 的响应，需要保存 verificationId
            // - way1/way2: 返回 null，不需要 verificationId
            const response = await sendVerifyCode(phone, countryCode);
            // 保存 verificationId（仅在使用 ycloud 时会有 id）
            if (response && response.id) {
                setVerificationId(response.id);
            } else {
                // way1/way2 方式不需要 verificationId
                setVerificationId(null);
            }
            Toast.show({ icon: 'success', content: 'Verification code sent' });
            setCountdown(COUNTDOWN_SECONDS);
            timerRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (error) {
            Toast.show({ icon: 'fail', content: error.message || 'Failed to send code, please try again later' });
        } finally {
            setSending(false);
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!name || name.trim().length === 0) {
            Toast.show({ content: 'Please enter your name' });
            return;
        }
        if (gender.length === 0) {
            Toast.show({ content: 'Please select your gender' });
            return;
        }
        if (!dateOfBirth) {
            Toast.show({ content: 'Please select your date of birth' });
            return;
        }
        if (!isValidInternationalPhone(countryCode, phone)) {
            Toast.show({ content: 'Please enter a valid phone number' });
            return;
        }
        if (!code) {
            Toast.show({ content: 'Please enter the verification code' });
            return;
        }


        const trimmedReferId = referId.trim();

        setRegistering(true);
        try {
            // Format date of birth to YYYY-MM-DD
            const formattedDOB = dateOfBirth
                ? `${dateOfBirth.getFullYear()}-${String(dateOfBirth.getMonth() + 1).padStart(2, '0')}-${String(dateOfBirth.getDate()).padStart(2, '0')}`
                : null;

            const { registerMemberWithVerification } = await import('../services/api');

            const result = await registerMemberWithVerification(
                name.trim(),
                gender[0],
                formattedDOB,
                phone,
                verificationId,
                code,
                trimmedReferId || null,
                countryCode,
                shopId,
                campaign || null,
            );

            const profile = await loginFromWsApiResult(result, {
                phone,
                nickname: name.trim(),
                shopId,
            });

            if (profile?.id == null || profile?.id === '') {
                throw new Error('Registration succeeded but member session is incomplete');
            }

            Toast.show({ icon: 'success', content: 'Registration successful!' });
            clearCampaignFromStorage();

            registrationJustSucceeded.current = true;
            markRegistrationRefreshPending();

            const redirect = searchParams.get('redirect');
            const target =
              redirect && redirect.startsWith('/') && !redirect.startsWith('//')
                ? redirect
                : '/';

            navigate(target, { replace: true });
        } catch (error) {
            Toast.show({ icon: 'fail', content: error.message || 'Registration failed, please try again later' });
        } finally {
            setRegistering(false);
        }
    };

    const sendCodeText = countdown > 0 ? `${countdown}s` : 'Send Code';

    const wsTokenInUrl = readWsTokenFromSearchParams(searchParams);
    if (wsTokenInUrl) {
        return <Navigate to={`/registerfromws?wstoken=${encodeURIComponent(wsTokenInUrl)}`} replace />;
    }

    if (initialized && isAuthenticated && !registrationJustSucceeded.current) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="register-page">
            <div className="register-card">
                <div className="register-header">
                    <h1 className="register-title">TaoGo Member Registration</h1>
                    <h2 className="register-subtitle">Register</h2>
                    <p className="register-description">Please fill in your details</p>
                </div>

                <div className="register-form">
                    <div className="form-section-title">Your Details</div>

                    <Form layout="vertical" footer={null}>
                        <Form.Item label={<span>Your Name<span className="required-mark">*</span></span>}>
                            <Input
                                value={name}
                                onChange={setName}
                                placeholder="Enter your name"
                                clearable
                            />
                        </Form.Item>

                        <Form.Item label={<span>Gender<span className="required-mark">*</span></span>}>
                            <div
                                onClick={() => setGenderPickerVisible(true)}
                                className="select-field"
                            >
                                {gender.length > 0
                                    ? genderOptions.find(opt => opt.value === gender[0])?.label
                                    : 'Select an option'}
                            </div>
                            <Picker
                                columns={[genderOptions]}
                                visible={genderPickerVisible}
                                onClose={() => setGenderPickerVisible(false)}
                                value={gender}
                                onConfirm={(v) => {
                                    setGender(v);
                                    setGenderPickerVisible(false);
                                }}
                            />
                        </Form.Item>

                        <Form.Item label={<span>Date of Birth<span className="required-mark">*</span></span>}>
                            <div
                                onClick={() => setDatePickerVisible(true)}
                                className="select-field"
                            >
                                {dateOfBirth
                                    ? dateOfBirth.toLocaleDateString('en-GB')
                                    : 'Select date'}
                            </div>
                            <DatePicker
                                visible={datePickerVisible}
                                onClose={() => setDatePickerVisible(false)}
                                value={dateOfBirth}
                                onConfirm={(v) => {
                                    setDateOfBirth(v);
                                    setDatePickerVisible(false);
                                }}
                                max={new Date()}
                                min={new Date(1926, 0, 1)}
                            />
                        </Form.Item>

                        <Form.Item label="Country Code (select)">
                            <CountryCodeSelect value={countryCode} onChange={setCountryCode} />
                        </Form.Item>

                        <Form.Item label="Phone Number">
                            <Input
                                type="tel"
                                maxLength={15}
                                value={phone}
                                onChange={(value) => setPhone(value.replace(/\D/g, '').slice(0, 14))}
                                placeholder="Enter phone number"
                                clearable
                            />
                        </Form.Item>

                        <Form.Item label="Verification Code">
                            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                <Input
                                    value={code}
                                    onChange={setCode}
                                    maxLength={6}
                                    placeholder="Enter verification code"
                                    style={{ flex: 1, minWidth: 0 }}
                                />
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={handleSendCode}
                                    loading={sending}
                                    disabled={countdown > 0}
                                    style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                >
                                    {sendCodeText}
                                </Button>
                            </div>
                        </Form.Item>

                        <Form.Item label="Referral ID (optional)">
                            <Input
                                value={referId}
                                onChange={setReferId}
                                placeholder="Inviter member id"
                                clearable
                            />
                        </Form.Item>
                    </Form>

                    <div className="checkbox-section">


                        <Checkbox
                            checked={marketingOptIn}
                            onChange={setMarketingOptIn}
                            style={{ '--icon-size': '18px' }}
                        >
                            <span className="checkbox-label">
                                I want to receive marketing campaigns and promotional offers.
                            </span>
                        </Checkbox>
                    </div>

                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        loading={registering}
                        block
                        className="register-button"
                    >
                        Register
                    </Button>

                    <div className="login-link">
                        Already have an account?{' '}
                        <a
                            href="/login"
                            onClick={(e) => {
                                e.preventDefault();
                                const ctx = mergeRegisterContextSearch(searchParams);
                                navigate({ pathname: '/login', search: ctx.toString() });
                            }}
                        >
                            Login
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
